import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  ClickAwayListener,
  Divider,
  Grid,
  Paper,
  Popper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Comment as CommentIcon } from "@mui/icons-material";

import PerfectScrollbar from "react-perfect-scrollbar";

import BellIcon from "assets/bell.svg?react";
import { INCOMLEND_INTERNAL_LINK, LEADS } from "codes";

import {
  useGetEntityNotificationsQuery,
  useGetNotificationsQuery,
  useUpdateMarkAsReadAllMutation,
} from "api/api-notifications";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import { useAppSelector } from "redux/hooks";
import MainCard from "components/Common/Cards/MainCard";
import Transitions from "components/Common/extended/Transitions";
import NoData from "components/Common/NoData";
import SkeletonLoad from "components/Common/SkeletonLoad";
import moment from "moment";
import { avatarStyles } from "styles/theme";
import NotificationList from "./NotificationList";
import NotificationTypeTabs from "./NotificationTypeTabs";
import LeadCommentsModal from "components/Common/LeadCommentsModal";
import { PERMISSIONS } from "permissions";
// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [open, setOpen] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);

  // State for lead comment modal
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [commentModalData, setCommentModalData] = useState<{
    leadId: string;
    leadName: string;
    accountId: string;
  } | null>(null);

  // Get current selected role/account id to scope notifications to entity
  const USER_ROLE = useAppSelector((state) => state.appState.role);
  const entityId = USER_ROLE?.id as string | undefined;
  const USER_PERMISSION = useAppSelector(
    (state) => state.appState.user_permission,
  );
  const invoice_reviewed = USER_PERMISSION.includes(
    PERMISSIONS?.invoice_reviewed,
  );
  // Fetch current user to check if they're internal
  const { data: currentUser } = useGetCurrentUserQuery();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  const handleCommentIconClick = () => {
    // For internal users, navigate to leads page
    if (currentUser?.is_internal) {
      navigate(`${INCOMLEND_INTERNAL_LINK}${LEADS}`);
    } else {
      // For clients, open the comment modal
      // Using the current entityId (client's own account ID)
      if (entityId) {
        setCommentModalData({
          leadId: entityId,
          leadName: currentUser
            ? `${currentUser.first_name} ${currentUser.last_name}`
            : "Client",
          accountId: entityId,
        });
        setIsCommentModalOpen(true);
      }
    }
  };

  // Fetch "my" notifications (header bell: mentions, etc.) and entity comment thread
  const { data: myNotifications = [] } = useGetNotificationsQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: entityNotifications = [], isLoading: loading } =
    useGetEntityNotificationsQuery(entityId || "", {
      skip: !entityId,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });
  const [markAllAsRead] = useUpdateMarkAsReadAllMutation();

  const prevOpen = useRef<boolean>(open);

  // Merge my notifications (map mentions, etc.) with entity comment thread; dedupe by id
  const notifications = useMemo(() => {
    // eslint-disable-next-line no-spaced-func, func-call-spacing -- typeof in type position
    const byId = new Map<string, (typeof myNotifications)[0]>();
    myNotifications.forEach((n) => byId.set(n.id, n));
    entityNotifications.forEach((n) => byId.set(n.id, n));
    return Array.from(byId.values());
  }, [myNotifications, entityNotifications, invoice_reviewed]);

  // Bell badge: only count "my" notifications (mentions, etc.), not thread comments
  const unreadCount: number =
    myNotifications?.filter((item) => !item.notif_is_read)?.length || 0;

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  // Listen for the custom event to open lead comment modal (from notification clicks)
  useEffect(() => {
    const handleOpenModal = () => {
      const leadId = sessionStorage.getItem("leadCommentModalId");
      const leadName = sessionStorage.getItem("leadCommentModalName") || "Lead";
      const accountId =
        sessionStorage.getItem("leadCommentEntityId") || entityId || "";

      if (leadId && accountId) {
        setCommentModalData({ leadId, leadName, accountId });
        setIsCommentModalOpen(true);
      }
    };

    window.addEventListener("openLeadCommentModal", handleOpenModal);
    return () => {
      window.removeEventListener("openLeadCommentModal", handleOpenModal);
    };
  }, [entityId]);

  const { last90Days = [], older = [] } = Object.create({
    last90Days: notifications
      ?.filter((notif) =>
        moment(notif.send_at).isAfter(moment().subtract(90, "days")),
      )
      ?.sort((a, b) => moment(b.send_at).diff(a.send_at)),
    older: notifications
      ?.filter((notif) =>
        moment(notif.send_at).isBefore(moment().subtract(90, "days")),
      )
      ?.sort((a, b) => moment(b.send_at).diff(a.send_at)),
  });

  return (
    <>
      <Box
        sx={{
          ml: 2,
          mr: 3,
          display: "flex",
          alignItems: "center",
          gap: 1,
          [theme.breakpoints.down("md")]: {
            mr: 2,
          },
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <ButtonBase sx={{ borderRadius: "12px" }}>
            <Avatar
              variant="rounded"
              sx={{
                ...avatarStyles.commonAvatar,
                ...avatarStyles.mediumAvatar,
                transition: "all .2s ease-in-out",
                background: theme.palette.background.default,
                color: theme.palette.primary.main,
                '&[aria-controls="menu-list-grow"],&:hover': {
                  background: theme.palette.primary.main,
                  color: theme.palette.background.default,
                },
              }}
              ref={anchorRef}
              aria-controls={open ? "menu-list-grow" : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
              color="inherit"
            >
              <BellIcon />
            </Avatar>
          </ButtonBase>
        </Badge>

        {/* Comment icon for clients only (not for internal users) */}
        {!currentUser?.is_internal && entityId && (
          <ButtonBase
            sx={{ borderRadius: "12px" }}
            onClick={handleCommentIconClick}
          >
            <Avatar
              variant="rounded"
              sx={{
                ...avatarStyles.commonAvatar,
                ...avatarStyles.mediumAvatar,
                transition: "all .2s ease-in-out",
                background: "#2196F3",
                color: "white",
                "&:hover": {
                  background: "#1976D2",
                  boxShadow: theme.shadows[6],
                },
                cursor: "pointer",
              }}
              color="inherit"
            >
              <CommentIcon />
            </Avatar>
          </ButtonBase>
        )}
      </Box>
      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [matchesXs ? 5 : 0, 20],
              },
            },
          ],
          strategy: "fixed",
        }}
      >
        {({ TransitionProps }) => (
          <Transitions
            position={matchesXs ? "top" : "top-right"}
            in={open}
            {...TransitionProps}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  border={false}
                  boxShadow
                  shadow={theme.shadows[16]}
                  content
                  title={
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ pt: 2, px: 2 }}
                    >
                      <Grid item>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Typography fontWeight={600} variant="h4">
                            Notification Centre
                          </Typography>
                          {notifications && (
                            <Avatar
                              sx={{
                                width: "1.5em",
                                height: "1.5em",
                                border: `1px solid ${theme.palette.secondary.main}`,
                                color: theme.palette.secondary.main,
                                bgcolor: "transparent",
                              }}
                              variant="rounded"
                            >
                              {notifications?.length}
                            </Avatar>
                          )}
                        </Stack>
                      </Grid>
                      {Boolean(notifications?.length) && (
                        <Grid item>
                          <Button
                            variant="text"
                            onClick={() =>
                              markAllAsRead().then(() => setOpen(false))
                            }
                            sx={{ textTransform: "inherit" }}
                          >
                            Mark as all read
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  }
                  headerSx={{ p: 1 }}
                  contentSX={{
                    background: theme.palette.background.paper,
                  }}
                >
                  {loading && <SkeletonLoad bars={10} />}
                  {!loading && (
                    <Grid
                      container
                      direction="column"
                      spacing={2}
                      id="notification-container"
                    >
                      <Grid item xs={12}>
                        <PerfectScrollbar
                          style={{
                            maxHeight: "60vh",
                            minHeight: "10vh",
                            overflowY: "scroll",
                          }}
                        >
                          <Stack spacing={1}>
                            <NotificationTypeTabs
                              notifications={last90Days}
                              onNotificationClick={() => setOpen(false)}
                            />
                            <Divider />
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color: theme.palette.inactive.main,
                                fontStyle: "italic",
                              }}
                            >
                              Older Notifications
                            </Typography>
                            <NotificationList
                              notifications={older}
                              onNotificationClick={() => setOpen(false)}
                            />
                          </Stack>
                        </PerfectScrollbar>
                      </Grid>
                      {notifications && !Boolean(notifications.length) && (
                        <NoData text="No new notifications" sx={{ mt: 2 }} />
                      )}
                    </Grid>
                  )}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>

      {/* Lead Comment Modal */}
      {commentModalData && (
        <LeadCommentsModal
          open={isCommentModalOpen}
          onClose={() => {
            setIsCommentModalOpen(false);
            // Clear session storage when modal is closed
            sessionStorage.removeItem("openLeadCommentModal");
            sessionStorage.removeItem("leadCommentModalId");
            sessionStorage.removeItem("leadCommentModalName");
            sessionStorage.removeItem("leadCommentEntityId");
            // Don't clear commentModalData so modal stays mounted
            // setCommentModalData(null);
          }}
          leadId={commentModalData.leadId}
          leadName={commentModalData.leadName}
          accountId={commentModalData.accountId}
        />
      )}
    </>
  );
};

export default NotificationSection;
